import { showToast } from "@certn/cascadia";
import { BasicButton } from "@certn/cascadia-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ActionToast } from "../../../components/action-toast/ActionToast";
import type { BulkMonitoringActionType } from "../types";
import {
  useBulkMonitoringAction,
  useRefreshDashboardApplicants,
} from "../useMonitoringApi";
import { DeactivateMonitoringConfirmModal } from "./DeactivateMonitoringConfirmModal";

interface ActionConfig {
  action: BulkMonitoringActionType;
  label: string;
}

/**
 * Derive the valid bulk actions from the monitoring statuses of the selected
 * rows (AUSPRO-672). Actions only appear when every selected applicant shares
 * one action family, so a mixed selection shows nothing:
 *  - Active only            → Pause / Deactivate
 *  - Paused only            → Resume
 *  - Ended only             → Renew
 *  - Anything else / mixed  → none
 */
function useActionsForSelection(selectedStatuses: string[]): ActionConfig[] {
  const { t } = useTranslation();
  const distinct = new Set(selectedStatuses);
  if (distinct.size === 0) return [];

  if (distinct.size === 1 && distinct.has("ACTIVE")) {
    return [
      { action: "PAUSE", label: t("Pause monitoring") },
      { action: "DEACTIVATE", label: t("Deactivate") },
    ];
  }
  if (distinct.size === 1 && distinct.has("PAUSED")) {
    return [{ action: "RESUME", label: t("Resume monitoring") }];
  }
  if (distinct.size === 1 && distinct.has("ENDED")) {
    return [{ action: "RENEW", label: t("Renew monitoring") }];
  }
  return [];
}

interface Props {
  selectedSubjectIds: string[];
  selectedStatuses: string[];
  onActionComplete: () => void;
}

export function MonitoringBulkActionBar({
  selectedSubjectIds,
  selectedStatuses,
  onActionComplete,
}: Props) {
  const { t } = useTranslation();
  const [pendingDeactivateSubjectIds, setPendingDeactivateSubjectIds] =
    useState<string[] | null>(null);
  const refreshDashboard = useRefreshDashboardApplicants();
  const actions = useActionsForSelection(selectedStatuses);
  const isDeactivateConfirmOpen = pendingDeactivateSubjectIds !== null;

  const { submit, isSubmitting } = useBulkMonitoringAction({
    onSuccess: (data) => {
      void refreshDashboard();
      showToast(t("Updated {{count}} applicants.", { count: data.updated }), {
        type: "success",
      });
      setPendingDeactivateSubjectIds(null);
      onActionComplete();
    },
  });

  useEffect(() => {
    if (pendingDeactivateSubjectIds === null) {
      return;
    }
    const selectionUnchanged =
      selectedSubjectIds.length === pendingDeactivateSubjectIds.length &&
      selectedSubjectIds.every(
        (id, index) => id === pendingDeactivateSubjectIds[index]
      );
    if (selectedSubjectIds.length === 0 || !selectionUnchanged) {
      setPendingDeactivateSubjectIds(null);
    }
  }, [pendingDeactivateSubjectIds, selectedSubjectIds]);

  const isOpen = selectedSubjectIds.length > 0;

  function handleDismiss() {
    setPendingDeactivateSubjectIds(null);
    onActionComplete();
  }

  function handlePress(action: BulkMonitoringActionType) {
    if (action === "DEACTIVATE") {
      setPendingDeactivateSubjectIds([...selectedSubjectIds]);
      return;
    }
    void submit({ subject_ids: selectedSubjectIds, action });
  }

  async function handleDeactivateConfirm() {
    if (!pendingDeactivateSubjectIds?.length) {
      return;
    }
    await submit({
      subject_ids: pendingDeactivateSubjectIds,
      action: "DEACTIVATE",
    });
  }

  return (
    <>
      <ActionToast
        isOpen={isOpen}
        onDismiss={handleDismiss}
        label={t("{{count}} selected", { count: selectedSubjectIds.length })}
        maxWidth="560px"
      >
        {actions.map((action) => (
          <BasicButton
            key={action.action}
            inverse
            isDisabled={isSubmitting}
            onPress={() => handlePress(action.action)}
            testID={`monitoring-bulk-action-${action.action.toLowerCase()}`}
          >
            {action.label}
          </BasicButton>
        ))}
      </ActionToast>
      <DeactivateMonitoringConfirmModal
        isOpen={isDeactivateConfirmOpen}
        onClose={() => setPendingDeactivateSubjectIds(null)}
        onConfirm={() => void handleDeactivateConfirm()}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
